import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Save, KeyRound } from "lucide-react";

export default function Account() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ name: "", phone: "", email: "", title: "" });
  const [pw, setPw] = useState({ current: "", new1: "", new2: "" });
  const [savingP, setSavingP] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", phone: user.phone || "", email: user.email || "", title: user.title || "" });
  }, [user]);

  const saveProfile = async () => {
    setSavingP(true);
    try {
      const { data } = await api.patch("/auth/me", profile);
      const stored = localStorage.getItem("tf_user");
      if (stored) {
        const u = { ...JSON.parse(stored), ...data };
        localStorage.setItem("tf_user", JSON.stringify(u));
      }
      toast.success("Profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingP(false); }
  };

  const changePassword = async () => {
    if (!pw.current || !pw.new1) { toast.error("Fill both password fields"); return; }
    if (pw.new1 !== pw.new2) { toast.error("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current: pw.current, new: pw.new1 });
      toast.success("Password changed");
      setPw({ current: "", new1: "", new2: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingPw(false); }
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Signed in as {user?.role}</div>
        <h1 className="font-display text-4xl tracking-tighter font-black">Account</h1>
        <p className="text-[#6E6E6A] text-sm mt-2">Update your login details and password.</p>

        <Card className="mt-8 border border-[#D8D8D5] shadow-none rounded-md p-6">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A] mb-4">Profile</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="acct-name" /></div>
            <div><Label>Title</Label><Input value={profile.title} onChange={(e) => setProfile({ ...profile, title: e.target.value })} placeholder="e.g. Co-founder" /></div>
            <div><Label>Phone (login)</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="acct-phone" /></div>
            <div><Label>Email</Label><Input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} /></div>
          </div>
          <Button onClick={saveProfile} disabled={savingP} data-testid="acct-save" className="mt-5 bg-[#1C1C1A] hover:bg-black text-white gap-2">
            <Save className="w-4 h-4" /> {savingP ? "Saving…" : "Save profile"}
          </Button>
        </Card>

        <Card className="mt-6 border border-[#D8D8D5] shadow-none rounded-md p-6">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A] mb-4">Change password</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Current</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" /></div>
            <div><Label>New</Label><Input type="password" value={pw.new1} onChange={(e) => setPw({ ...pw, new1: e.target.value })} data-testid="pw-new" /></div>
            <div><Label>Confirm new</Label><Input type="password" value={pw.new2} onChange={(e) => setPw({ ...pw, new2: e.target.value })} data-testid="pw-confirm" /></div>
          </div>
          <Button onClick={changePassword} disabled={savingPw} data-testid="pw-save" className="mt-5 bg-[#1C1C1A] hover:bg-black text-white gap-2">
            <KeyRound className="w-4 h-4" /> {savingPw ? "Updating…" : "Update password"}
          </Button>
        </Card>
      </div>
    </div>
  );
}
